import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class DmsAuthService {
  // DMS JWT Secret - WARNING: This is visible in frontend code
  private readonly DMS_JWT_SECRET = 'Y8TQw9xD4B7C6fZ0S2aM3EJmV5rHkLUpWnNKRXq1eIboPAcFZyHsGdOt';

  constructor() {}

  /**
   * Generate a JWT token for DMS API calls
   * @param payload - The payload to include in the token
   * @param expiresInSeconds - Token expiration time in seconds (default: 1 hour)
   * @returns JWT token string
   */
  generateDmsToken(payload: any, expiresInSeconds: number = 3600): string {
    const header = {
      alg: 'HS256',
      typ: 'JWT'
    };

    const now = Math.floor(Date.now() / 1000);
    const tokenPayload = {
      ...payload,
      iat: now,
      exp: now + expiresInSeconds
    };

    // Base64Url encode header and payload
    const encodedHeader = this.base64UrlEncode(JSON.stringify(header));
    const encodedPayload = this.base64UrlEncode(JSON.stringify(tokenPayload));

    // Create signature
    const signatureInput = `${encodedHeader}.${encodedPayload}`;
    const signature = this.createSignature(signatureInput);

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  /**
   * Generate a DMS token using current user info from sessionStorage
   * @returns JWT token string for DMS API
   */
  generateDmsTokenFromCurrentUser(): string {
    try {
      const userStr = sessionStorage.getItem('user-info');
      if (!userStr) {
        console.warn('[DmsAuthService] No user-info found in sessionStorage');
        return '';
      }

      const user = JSON.parse(userStr);
      console.log('[DmsAuthService] User from sessionStorage:', user);
      
      // Get site_id from sessionStorage if available
      const currentSite = sessionStorage.getItem('current-site');
      let siteId = null;
      if (currentSite) {
        try {
          const site = JSON.parse(currentSite);
          siteId = site.site_id || site.siteId || null;
        } catch {
          siteId = currentSite; // If it's just a plain value
        }
      }
      
      // Build payload matching the DMS backend JWT structure
      const payload = {
        id: String(user.id || ''),
        email: user.email || '',
        user_role: String(user.user_role || user.role || ''),
        first_name: user.first_name || '',
        corporation_id: user.corporation_id || '',
        site_id: siteId,
        aud: 'purebi-dms-server',  // Audience claim required by DMS backend
        iss: 'https://serverdms.pure.bi'  // Issuer claim required by DMS backend
      };

      console.log('[DmsAuthService] Generated payload:', payload);
      const token = this.generateDmsToken(payload);
      console.log('[DmsAuthService] Generated DMS token:', token.substring(0, 50) + '...');
      
      return token;
    } catch (error) {
      console.error('[DmsAuthService] Error generating DMS token:', error);
      return '';
    }
  }

  /**
   * Base64Url encode a string (JWT-compatible base64)
   */
  private base64UrlEncode(str: string): string {
    const base64 = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));
    // Convert to Base64Url format
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Create HMAC-SHA256 signature for JWT
   */
  private createSignature(input: string): string {
    const signature = CryptoJS.HmacSHA256(input, this.DMS_JWT_SECRET);
    const base64Signature = CryptoJS.enc.Base64.stringify(signature);
    // Convert to Base64Url format
    return base64Signature
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}

